import { Ban } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import { useGroupUsers } from "~/features/admin/groups/hooks";
import { toGroupUserRowList } from "~/features/admin/groups/utils/transformers";

interface GroupUsersProps {
  groupId: string;
}

/**
 * Lists the users belonging to a group. V1 rendered the full set in a simple,
 * non-paginated table — preserved here.
 */
export function GroupUsers({ groupId }: GroupUsersProps) {
  const { t } = useTranslation();
  const { data } = useGroupUsers(groupId);
  const users = useMemo(() => toGroupUserRowList(data.users), [data.users]);

  if (users.length === 0) {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-3">
        {t("ui.notFound")}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border-default">
            <th className="px-3.5 py-2 text-left text-12-medium text-fg-2">{t("model.user.displayName")}</th>
            <th className="px-3.5 py-2 text-left text-12-medium text-fg-2">{t("model.user.email")}</th>
            <th className="px-3.5 py-2 text-left text-12-medium text-fg-2">{t("model.user.segments")}</th>
            <th className="px-3.5 py-2 text-left text-12-medium text-fg-2">{t("ui.status")}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, i) => (
            <tr key={user.id} className={i < users.length - 1 ? "border-b border-border-default" : undefined}>
              <td className="px-3.5 py-2 text-14-regular text-fg-1">
                <span className="inline-flex items-center gap-1.5">
                  {user.isDeactivated && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Ban className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
                      </TooltipTrigger>
                      <TooltipContent>{t("ui.deactivated")}</TooltipContent>
                    </Tooltip>
                  )}
                  <span>{user.displayName || "—"}</span>
                </span>
              </td>
              <td className="px-3.5 py-2 text-14-regular text-fg-2">{user.email}</td>
              <td className="px-3.5 py-2 text-14-regular text-fg-2">{user.segments.join(" / ") || "—"}</td>
              <td className="px-3.5 py-2 text-14-regular text-fg-2">
                {user.isDeactivated ? t("ui.inactive") : t("ui.active")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
